import * as React from 'react';

import { requestGet } from '../../../modules/request';
import useWindowSize from '../../Shared/hooks/useWindowSize';
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor';
import { Spinner, Snackbar, defaultSnackbarTimeout, getPageTitle, } from '../../Shared/index';
import ActivityFeed from '../components/dashboard/activity_feed';
import DailyTinyTip from '../components/dashboard/daily_tiny_tip';
import DemoModal from '../components/dashboard/demo_modal';
import DiagnosticMini from '../components/dashboard/diagnostic_mini';
import BulkArchiveClassesCard from '../components/dashboard/bulk_archive_classrooms_card'
import HandyActions from '../components/dashboard/handy_actions';
import KeyMetrics from '../components/dashboard/key_metrics';
import LessonsMini from '../components/dashboard/lessons_mini';
import LiveViewMini from '../components/dashboard/live_view_mini';
import OnboardingChecklist from '../components/dashboard/onboarding_checklist';
import TeacherCenterHighlights from '../components/dashboard/teacher_center_highlights';
import ContentHubCard, { WORLD_HISTORY, AI_KNOWLEDGE, LITERATURE, } from '../components/dashboard/content_hub_card'
import TeacherInfoModal from '../components/dashboard/teacher_info_modal';
import WelcomeModal from '../components/dashboard/welcome_modal';
import ArticleSpotlight from '../components/shared/articleSpotlight';
import DemoOnboardingTour, { DEMO_ONBOARDING_DASHBOARD, } from '../components/shared/demo_onboarding_tour';
import { TEACHER_DASHBOARD_FEATURED_BLOG_POST_ID } from '../constants/featuredBlogPost';

const MAX_VIEW_WIDTH_FOR_MOBILE = 1103

const Dashboard = ({ isCollege, onboardingChecklist, firstName, mustSeeWelcomeModal, mustSeeTeacherInfoModal, linkedToClever, featuredBlogPosts, showEvidencePromotionCard, subjectAreas, userId, classrooms, hasTeacherPremium, hasSchoolOrDistrictPremium, premiumState, liveViewEnabled, }) => {

  document.title = getPageTitle("Home", "Overview")
  const size = useWindowSize();
  const className = "dashboard white-background-accommodate-footer"
  const onMobile = () => size.width <= MAX_VIEW_WIDTH_FOR_MOBILE

  const [showWelcomeModal, setShowWelcomeModal] = React.useState(mustSeeWelcomeModal)
  const [showTeacherInfoModal, setShowTeacherInfoModal] = React.useState(mustSeeTeacherInfoModal)
  const [showDemoModal, setShowDemoModal] = React.useState(false)
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarText, setSnackbarText] = React.useState('');

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  function closeWelcomeModal() { setShowWelcomeModal(false) }
  function closeDemoModal() { setShowDemoModal(false) }
  function closeTeacherInfoModal() { setShowTeacherInfoModal(false) }

  if (!onboardingChecklist.every(obj => obj.checked)) {
    return (
      <div className={className} id="post-secondary-nav">
        {showWelcomeModal && <WelcomeModal close={closeWelcomeModal} size={size} />}
        {showDemoModal && <DemoModal close={closeDemoModal} size={size} />}
        <OnboardingChecklist firstName={firstName} onboardingChecklist={onboardingChecklist} />
      </div>
    )
  }

  const [metrics, setMetrics] = React.useState(null)
  const [diagnostics, setDiagnostics] = React.useState(null);
  const [lessons, setLessons] = React.useState(null);
  const [activityFeed, setActivityFeed] = React.useState(null)
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    getMetrics();
    getDiagnostics()
    getLessons()
    getActivityFeed()
  }, []);

  React.useEffect(() => {
    if (metrics && diagnostics && lessons && activityFeed && loading) {
      setLoading(false)
    }
  }, [metrics, diagnostics, lessons, activityFeed])

  function getMetrics() {
    requestGet('/teacher_dashboard_metrics',
      (data) => {
        setMetrics(data);
      }
    )
  }

  function getDiagnostics() {
    requestGet('/teachers/diagnostic_info_for_dashboard_mini',
      (data) => {
        setDiagnostics(data.units);
      }
    )
  }

  function getLessons() {
    requestGet('/teachers/lessons_info_for_dashboard_mini',
      (data) => {
        setLessons(data.units);
      }
    )
  }

  function getActivityFeed() {
    requestGet('/teachers/activity_feed',
      (response) => {
        setActivityFeed(response.data);
      }
    )
  }

  function handleBulkArchiveSuccess(snackbarCopy) {
    setSnackbarText(snackbarCopy)
    setShowSnackbar(true)
  }

  if (loading) {
    return (
      <div className={className}>
        <div className="post-checklist-container loading">
          <Spinner />
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <Snackbar text={snackbarText} visible={showSnackbar} />
      <div className={className} id="post-secondary-nav">
        <div className="post-checklist-container">
          <DemoOnboardingTour
            pageKey={DEMO_ONBOARDING_DASHBOARD}
          />
          {showDemoModal && <DemoModal close={closeDemoModal} size={size} />}
          {showTeacherInfoModal && <TeacherInfoModal close={closeTeacherInfoModal} isCollege={isCollege} subjectAreas={subjectAreas} />}
          <main>
            <BulkArchiveClassesCard
              classrooms={classrooms}
              onSuccess={handleBulkArchiveSuccess}
              userId={userId}
            />
            <KeyMetrics firstName={firstName} metrics={metrics} />
            {liveViewEnabled && <LiveViewMini fullAccess={hasSchoolOrDistrictPremium} />}
            <DiagnosticMini diagnostics={diagnostics} onMobile={onMobile()} />
            <LessonsMini lessons={lessons} onMobile={onMobile()} />
            <ActivityFeed activityFeed={activityFeed} onMobile={onMobile()} />
          </main>
          <aside>
            <HandyActions linkedToClever={linkedToClever} setShowDemoModal={setShowDemoModal} />
            <DailyTinyTip />
            <TeacherCenterHighlights featuredBlogPosts={featuredBlogPosts} />
            <ContentHubCard contentHub={LITERATURE} />
            <ContentHubCard contentHub={WORLD_HISTORY} />
            <ContentHubCard contentHub={AI_KNOWLEDGE} />
          </aside>
        </div>
      </div>
      <ArticleSpotlight blogPostId={TEACHER_DASHBOARD_FEATURED_BLOG_POST_ID} />
    </React.Fragment>
  )

}

export default Dashboard
